import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, Loader2, Menu, X, KeyRound } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function ForcePasswordReset() {
  const { logout, refresh } = useAuth();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd !== confirm) { toast.error("Passwords do not match."); return; }
    if (pwd.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { new_password: pwd });
      toast.success("Password updated! Logging you in…");
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full space-y-6">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
            <KeyRound size={26} className="text-primary" />
          </div>
          <h1 className="font-display text-3xl text-primary">Set New Password</h1>
          <p className="text-slate-500 text-sm">You're using a temporary password. Please set a new one to continue.</p>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">New Password</label>
            <Input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="At least 6 characters"
              className="rounded-xl"
              data-testid="force-reset-pwd"
              autoFocus
            />
          </div>
          <div className="space-y-1">
            <label className="text-sm font-semibold text-slate-700">Confirm Password</label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Repeat your new password"
              className="rounded-xl"
              data-testid="force-reset-confirm"
            />
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full rounded-full font-display text-base py-5 bg-primary hover:bg-primary/90"
            data-testid="force-reset-submit"
          >
            {loading ? <Loader2 size={18} className="animate-spin mr-2" /> : null}
            Set Password & Continue
          </Button>
        </form>
        <button onClick={logout} className="w-full text-sm text-slate-400 hover:text-slate-600 underline">
          Log out instead
        </button>
      </div>
    </div>
  );
}

export function ProtectedRoute({ role, children }) {
  const { user, loaded } = useAuth();
  if (!loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-primary" size={40} />
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  const allowed = Array.isArray(role) ? role : [role];
  if (role && !allowed.includes(user.role)) {
    const dest = ["admin", "staff"].includes(user.role) ? "/portal/admin" : "/portal/members";
    return <Navigate to={dest} replace />;
  }
  if (user.must_change_password) {
    return <ForcePasswordReset />;
  }
  return children;
}

export function PortalShell({ title, tabs, active, onTab, children }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const NavItem = ({ t }) => (
    <button
      key={t.key}
      data-testid={`tab-${t.key}`}
      onClick={() => { onTab(t.key); setMobileOpen(false); }}
      className={`flex items-center gap-3 rounded-xl px-4 py-2.5 text-left w-full transition-colors font-medium text-sm ${
        active === t.key
          ? "bg-primary text-white shadow-sm"
          : "text-slate-600 hover:bg-primary/10 hover:text-primary"
      }`}
    >
      {t.icon && <t.icon size={17} />}
      {t.label}
    </button>
  );

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:bg-primary/10 hover:text-primary transition-colors"
              onClick={() => setMobileOpen(true)}
              data-testid="mobile-nav-open"
              aria-label="Open navigation"
            >
              <Menu size={22} />
            </button>
            <div className="brand-logo text-2xl sm:text-3xl">FUN-NASTIX</div>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm text-slate-500 hidden sm:inline">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={logout} data-testid="portal-logout" className="rounded-full">
              <LogOut size={16} className="mr-1" /> Log out
            </Button>
          </div>
        </div>
      </header>

      {/* Mobile nav drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          {/* Drawer */}
          <div className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="brand-logo text-2xl">FUN-NASTIX</div>
              <button
                className="p-1.5 rounded-xl text-slate-500 hover:bg-slate-100 transition-colors"
                onClick={() => setMobileOpen(false)}
                data-testid="mobile-nav-close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-5 pt-4 pb-2">
              <span className="font-display text-lg text-primary">{title}</span>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 pb-6 space-y-0.5">
              {tabs.map((t) => <NavItem key={t.key} t={t} />)}
            </nav>
            <div className="px-5 py-4 border-t border-border">
              <span className="text-xs text-slate-400">{user?.name}</span>
            </div>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-7xl mx-auto w-full px-4 py-6 flex-1 flex flex-col lg:flex-row gap-6">
        {/* Desktop sidebar */}
        <aside className="lg:w-56 shrink-0 hidden lg:flex lg:flex-col">
          <h1 className="font-display text-2xl text-primary mb-4">{title}</h1>
          <nav className="flex flex-col gap-0.5">
            {tabs.map((t) => <NavItem key={t.key} t={t} />)}
          </nav>
        </aside>

        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}
